import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../Contexts/ApiContext';
import MarkdownTextPresenter from '../MarkdownTextPresenter';
import { useApplication } from '../../Contexts/ApplicationContext';
import CopyToClipButton from '../CopyToClipButton';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
};

const TranscriptionHistory = () => {

    const { getHistoricalTranscriptionsAsync, getCaseNotesAsync, getSummaryAsync} = useContext(ApiContext);

    const [transcriptions, setTranscriptions] = useState([]);
    const [searchInput, setSearchInput] = useState(''); // Added searchInput state
    const debouncedSearchTerm = useDebounce(searchInput, 300); // Use searchInput here
    const [loading, setLoading] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 10,
        totalCount: 0
    });
    const [selectedTranscription, setSelectedTranscription] = useState(null);
    const [selectedCaseNotes, setSelectedCaseNotes] = useState(false);
    const [selectedGpLetter, setSelectedGpLetter] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('caseNotes'); // 'caseNotes' or 'transcription'
    const [transcriptionText, setTranscriptionText] = useState('');
    const { setError } = useApplication();


    useEffect(() => {
        fetchTranscriptions();
    }, [pagination.page, debouncedSearchTerm]);

    useEffect(() => {
        setActiveTab("transcription");
        setSelectedCaseNotes("");
        setSelectedGpLetter("");
        handleCaseNotes();
        handleGpLetter();
    }, [selectedTranscription]);

    const handleCaseNotes = async () => {
        try {
            setLoadingModal(true);
            if (selectedTranscription?.id) {
                const notes = await getCaseNotesAsync(selectedTranscription?.id);
                setSelectedCaseNotes(notes);
            }
            //setLoadingModal(false);
        }
        catch (err) {
            setLoadingModal(false);
            setError(err.message || 'Failed to fetch case notes');

        }
    }

    const handleGpLetter = async () => {
        try {
            setLoadingModal(true);
            if (selectedTranscription?.id) {
                const letter = await getSummaryAsync(selectedTranscription?.id);
                setSelectedGpLetter(letter);
            }
            setLoadingModal(false);
        }
        catch (err) {
            setLoadingModal(false);
            setError(err.message || 'Failed to fetch GP letter');
        }
    }

    const fetchTranscriptions = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await getHistoricalTranscriptionsAsync(
                pagination.page,
                pagination.perPage,
                debouncedSearchTerm
            );

            setTranscriptions(response.data);
            setPagination({
                page: response.page,
                perPage: response.perPage,
                totalCount: response.totalCount
            });
        } catch (err) {
            setError(err.message || 'Failed to fetch transcriptions');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchInput(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const handlePreviewClick = async (transcription) => {
        setSelectedTranscription(transcription);
        setIsPreviewOpen(true);
        try {
            setLoadingModal(true);
            // Assuming there's an API endpoint to fetch transcription text
            const notes = await getCaseNotesAsync(transcription?.id);
            const transcriptText = transcription?.transcription || 'No transcription available'; // Replace with actual API call if needed
            setSelectedCaseNotes(notes);
            setTranscriptionText(transcriptText);
            setLoadingModal(false);
        } catch (err) {
            setLoadingModal(false);
        }
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
        setSelectedTranscription(null);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    return (
        <div className=" pl-4 pr-4 overflow-y-auto h-full">
            <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-semibold ml-4 mt-[20px] flex items-center text-left">Transcription History</h2>
                <input
                    type="text"
                    placeholder="Search by name or appointment number..."
                    value={searchInput}
                    onChange={handleSearch}
                    className="px-4 py-2 mr-4 border border-gray-300 rounded-md w-full max-w-md"
                />
            </div>
        
            {loading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-3">Loading transcriptions...</span>
                </div>
            ) : (
                <div>
                <table className="min-w-full bg-white border border-gray-300">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="px-6 py-2 border-b text-left">Date</th>
                            <th className="px-6 py-2 border-b text-left">Name</th>
                            <th className="px-6 py-2 border-b text-left">Appointment Number</th>
                            <th className="px-6 py-2 border-b text-left">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transcriptions.map((transcription) => (
                            <tr key={transcription?.id} className="hover:bg-gray-50">
                                <td className="px-6 py-2 border-b text-left">
                                    {formatDate(transcription?.createdOn)}
                                </td>
                                <td className="px-6 py-2 border-b text-left">
                                    {transcription.createdBy}
                                </td>
                                <td className="px-6 py-2 border-b text-left">
                                    {transcription.reference}
                                </td>
                                <td className="px-6 py-2 border-b text-left">
                                    <button
                                        onClick={() => handlePreviewClick(transcription)}
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>


            {/* Pagination Controls */}
            <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.perPage) + 1} to{' '}
                    {Math.min(pagination.page * pagination.perPage, pagination.totalCount)} of{' '}
                    {pagination.totalCount} results
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className={`px-3 py-1 rounded-md ${
                            pagination.page === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex space-x-1">
                        {[...Array(Math.ceil(pagination.totalCount / pagination.perPage))].map((_, index) => {
                            const pageNumber = index + 1;
                            // Show current page, first page, last page, and one page before and after current
                            if (
                                pageNumber === 1 ||
                                pageNumber === Math.ceil(pagination.totalCount / pagination.perPage) ||
                                (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)
                            ) {
                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`px-3 py-1 rounded-md ${
                                            pagination.page === pageNumber
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                        }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            } else if (
                                pageNumber === pagination.page - 2 ||
                                pageNumber === pagination.page + 2
                            ) {
                                return <span key={pageNumber} className="px-2">...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= Math.ceil(pagination.totalCount / pagination.perPage)}
                        className={`px-3 py-1 rounded-md ${
                            pagination.page >= Math.ceil(pagination.totalCount / pagination.perPage)
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Next
                    </button>
                </div>
            </div>

                </div>
            )}





            {/* Preview Modal */}
            {isPreviewOpen && selectedTranscription && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-8xl w-full mx-4 max-h-[95vh] overflow-y-auto">
                        {loadingModal ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                                <span className="ml-3">Loading...</span>
                            </div>
                        ) : (
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">Session View</h3>
                                    <button
                                        onClick={closePreview}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600">Created by: {selectedTranscription.createdBy}</p>
                                    <p className="text-sm text-gray-600">Date: {formatDate(selectedTranscription.createdOn)}</p>
                                </div>

                                {/* Add tabs */}
                                <div className="border-b border-gray-200 mb-4">
                                    <nav className="-mb-px flex">
                                        <button
                                            className={`py-2 px-4 font-medium ${
                                                activeTab === 'transcription'
                                                    ? 'border-b-2 border-blue-500 text-blue-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                            onClick={() => setActiveTab('transcription')}
                                        >
                                            Transcription
                                        </button>

                                        <button
                                            className={`mr-4 py-2 px-4 font-medium ${
                                                activeTab === 'caseNotes'
                                                    ? 'border-b-2 border-blue-500 text-blue-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                            onClick={() => setActiveTab('caseNotes')}
                                        >
                                            Case Notes
                                        </button>
                                        <button
                                            className={`mr-4 py-2 px-4 font-medium ${
                                                activeTab === 'gpLetter'
                                                    ? 'border-b-2 border-blue-500 text-blue-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                            onClick={() => setActiveTab('gpLetter')}
                                        >
                                            GP Letter
                                        </button>
                                        
                                    </nav>
                                </div>

                                <div className="prose max-w-none relative">
                                    {activeTab === 'transcription' && (
                                        <div className="whitespace-pre-wrap">
                                            {transcriptionText}
                                            <CopyToClipButton text={transcriptionText} />
                                        </div>
                                    )}
                                    {activeTab === 'caseNotes' && (
                                        <MarkdownTextPresenter markdown={selectedCaseNotes} consent={true} />
                                    )}
                                    {activeTab === 'gpLetter' && (
                                        <MarkdownTextPresenter markdown={selectedGpLetter} />
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={closePreview}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            

            {transcriptions.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                    No transcriptions found
                </div>
            )}
        </div>
    );
};

export default TranscriptionHistory; 